<template>
  <div class="settings-page">
    <v-container class="fade-in">
      <v-card class="mb-6 pa-4">
        <v-card-title class="pb-2 px-0">
          <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
          <span class="text-h5 font-weight-medium primary--text">Impostazioni</span>
        </v-card-title>
        <v-divider class="mb-4"></v-divider>

        <!-- Sezione Tema -->
        <v-card-subtitle class="px-0 pb-2 text-subtitle-1 font-weight-medium">
          <v-icon color="primary" class="mr-2">mdi-palette</v-icon>
          Tema
        </v-card-subtitle>

        <v-card-text class="px-0">
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="selectedPalette"
                :items="paletteItems"
                label="Palette di colori"
                single-line
                hide-details
                @change="changeColorPalette"
                return-object
              >
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <div class="color-palette-preview mr-2">
                      <div class="color-preview" :style="{ backgroundColor: getColorFromPalette(item.value, 'primary') }"></div>
                      <div class="color-preview" :style="{ backgroundColor: getColorFromPalette(item.value, 'secondary') }"></div>
                      <div class="color-preview" :style="{ backgroundColor: getColorFromPalette(item.value, 'accent') }"></div>
                    </div>
                    {{ item.text }}
                  </div>
                </template>
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center">
                    <div class="color-palette-preview mr-2">
                      <div class="color-preview" :style="{ backgroundColor: getColorFromPalette(item.value, 'primary') }"></div>
                      <div class="color-preview" :style="{ backgroundColor: getColorFromPalette(item.value, 'secondary') }"></div>
                      <div class="color-preview" :style="{ backgroundColor: getColorFromPalette(item.value, 'accent') }"></div>
                    </div>
                    {{ item.text }}
                  </div>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="selectedFont"
                :items="fontItems"
                label="Font"
                single-line
                hide-details
                @change="changeFontFamily"
              >
                <template v-slot:selection="{ item }">
                  <div :style="{ fontFamily: item.value }">{{ item.text }}</div>
                </template>
                <template v-slot:item="{ item }">
                  <div :style="{ fontFamily: item.value }">{{ item.text }}</div>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Anteprima -->
        <v-card-subtitle class="px-0 pb-2 text-subtitle-1 font-weight-medium">
          <v-icon color="primary" class="mr-2">mdi-eye</v-icon>
          Anteprima
        </v-card-subtitle>

        <v-card-text class="px-0">
          <v-card class="preview-card mb-4" :style="{ fontFamily: selectedFont }">
            <v-card-title class="white--text" :style="{ backgroundColor: getColorFromPalette(selectedPalette, 'primary') }">
              Titolo della Card
            </v-card-title>
            <v-card-text>
              <p>Questo è un esempio di testo con il font selezionato.</p>
              <div class="d-flex flex-wrap">
                <v-btn class="mr-2 mb-2" color="primary" text>Primario</v-btn>
                <v-btn class="mr-2 mb-2" color="secondary" text>Secondario</v-btn>
                <v-btn class="mr-2 mb-2" color="accent" text>Accento</v-btn>
                <v-btn class="mr-2 mb-2" color="error" text>Errore</v-btn>
                <v-btn class="mr-2 mb-2" color="success" text>Successo</v-btn>
                <v-btn class="mr-2 mb-2" color="warning" text>Avviso</v-btn>
                <v-btn class="mr-2 mb-2" color="info" text>Info</v-btn>
              </div>
              <div class="d-flex flex-wrap mt-4">
                <v-chip class="mr-2 mb-2" color="primary" text-color="white">Primario</v-chip>
                <v-chip class="mr-2 mb-2" color="secondary" text-color="white">Secondario</v-chip>
                <v-chip class="mr-2 mb-2" color="accent" text-color="white">Accento</v-chip>
                <v-chip class="mr-2 mb-2" color="error" text-color="white">Errore</v-chip>
                <v-chip class="mr-2 mb-2" color="success" text-color="white">Successo</v-chip>
                <v-chip class="mr-2 mb-2" color="warning" dark>Avviso</v-chip>
                <v-chip class="mr-2 mb-2" color="info" text-color="white">Info</v-chip>
              </div>
            </v-card-text>
          </v-card>

          <v-alert type="info" text>
            Le modifiche al tema verranno applicate immediatamente e salvate per le sessioni future.
          </v-alert>
        </v-card-text>
      </v-card>

      <!-- Impostazioni Tabelle -->
      <table-settings></table-settings>
    </v-container>
  </div>
</template>

<script>
import SettingsService from '@/services/settings.service.js';
import TableSettings from '@/components/settings/TableSettings.vue';

export default {
  name: 'Settings',
  components: {
    TableSettings
  },
  data() {
    return {
      selectedPalette: { text: 'Blu Oceano', value: 'blueOcean' },
      selectedFont: 'Montserrat, sans-serif',
      paletteItems: [],
      fontItems: []
    };
  },
  created() {
    // Prepara gli item per le select
    this.paletteItems = Object.keys(SettingsService.colorPalettes).map(key => ({
      text: SettingsService.colorPalettes[key].name,
      value: key
    }));

    this.fontItems = SettingsService.availableFonts.map(font => ({
      text: font.name,
      value: font.value
    }));

    // Carica le impostazioni salvate
    const settings = SettingsService.loadSettings();
    const savedPalette = settings.colorPalette || 'blueOcean';
    this.selectedPalette = this.paletteItems.find(item => item.value === savedPalette) || this.paletteItems.find(item => item.value === 'blueOcean');
    this.selectedFont = settings.fontFamily || 'Montserrat, sans-serif';
  },
  methods: {
    /**
     * Cambia la palette di colori
     */
    changeColorPalette() {
      SettingsService.setColorPalette(this.selectedPalette.value);

      // Forza l'aggiornamento del tema di Vuetify
      setTimeout(() => {
        // Ricarica la pagina per applicare completamente i cambiamenti
        window.location.reload();
      }, 500);

      this.$forceUpdate(); // Forza l'aggiornamento dell'interfaccia
    },

    /**
     * Cambia il font dell'applicazione
     */
    changeFontFamily() {
      SettingsService.setFontFamily(this.selectedFont);

      // Ricarica la pagina per applicare completamente i cambiamenti
      setTimeout(() => {
        window.location.reload();
      }, 500);
    },

    /**
     * Ottiene un colore specifico dalla palette selezionata
     * @param {string} palette - Il nome della palette
     * @param {string} colorName - Il nome del colore
     * @returns {string} Il colore in formato esadecimale
     */
    getColorFromPalette(palette, colorName) {
      if (SettingsService.colorPalettes[palette] && SettingsService.colorPalettes[palette].colors[colorName]) {
        return SettingsService.colorPalettes[palette].colors[colorName];
      }
      return '#000000';
    }
  },
  watch: {
    // Osserva i cambiamenti delle impostazioni
    selectedPalette: {
      handler() {
        this.$forceUpdate();
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.settings-page {
  padding-bottom: 40px;
}

.color-palette-preview {
  display: flex;
  align-items: center;
}

.color-preview {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin-right: 4px;
}

.preview-card {
  border: 1px solid rgba(0, 0, 0, 0.1);
}
</style>
